import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { faXmark as fasXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { CMButton } from "@chartmetric/chartmetric-design-system/components/CMButton";
import { CMFlex } from "@chartmetric/chartmetric-design-system/components/CMFlex";
import { CMText } from "@chartmetric/chartmetric-design-system/components/CMText";
import { CMInput } from "@chartmetric/chartmetric-design-system/components/CMInput";

import { useAuth } from "../contexts/AuthContext";
import { ApiError } from "../api/client";

function SignInModal() {
  const { t } = useTranslation("common", { keyPrefix: "auth" });
  const { isAuthModalOpen, closeAuthModal, signIn } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthModalOpen) {
      setEmail("");
      setPassword("");
      setCode("");
      setShowCodeInput(false);
      setErrorKey(null);
      setIsSubmitting(false);
    }
  }, [isAuthModalOpen]);

  useEffect(() => {
    if (!isAuthModalOpen) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") closeAuthModal();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isAuthModalOpen, closeAuthModal]);

  if (!isAuthModalOpen) return null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email || !password) return;
    setErrorKey(null);
    setIsSubmitting(true);
    try {
      const result = await signIn(email, password, code || undefined);
      if (result.requiresCode) {
        setShowCodeInput(true);
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 400 || err.status === 401)) {
        setErrorKey("invalidCredentials");
      } else {
        setErrorKey("unknownError");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CMFlex
      position="fixed"
      align="center"
      justify="center"
      _className="inset-0 z-50 bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={closeAuthModal}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="w-full max-w-md rounded-lg bg-theme-surface p-6 shadow-xl"
      >
        <CMFlex vertical gap="md">
          <CMFlex align="center" justify="between">
            <CMText value={t("signInTitle")} variant="h4" />
            <CMButton
              type="button"
              iconLeft={fasXmark}
              variant="secondaryText"
              size="sm"
              onClick={closeAuthModal}
              aria-label={t("close")}
            />
          </CMFlex>
          <CMText
            value={t("signInDescription")}
            variant="mini-1"
            color="tertiary"
          />
          <CMInput
            id="signin-email"
            type="email"
            label={t("emailLabel")}
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isSubmitting}
          />
          <CMInput
            id="signin-password"
            type="password"
            label={t("passwordLabel")}
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
          />
          {showCodeInput && (
            <CMFlex vertical gap="xs">
              <CMInput
                id="signin-code"
                type="text"
                label={t("codeLabel")}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={isSubmitting}
              />
              <CMText value={t("codeHelp")} variant="mini-1" color="tertiary" />
            </CMFlex>
          )}
          {errorKey && (
            <CMText value={t(errorKey)} variant="mini-1" color="error" />
          )}
          <CMButton
            type="submit"
            text={isSubmitting ? t("signingIn") : t("signIn")}
            variant="primary"
            size="md"
            fullWidth
            isLoading={isSubmitting}
            disabled={isSubmitting}
          />
        </CMFlex>
      </form>
    </CMFlex>
  );
}

export default SignInModal;
